import React from 'react';
import { View, Text as RNText, StyleSheet, Pressable } from 'react-native';
import { apm } from '@/lib/apm';

/**
 * React Error Boundary — catches unhandled JS errors and shows a fallback UI
 * instead of crashing the entire app on iOS/Android.
 *
 * IMPORTANT: This component sits OUTSIDE ThemeProvider, so the fallback UI
 * must only use plain React Native primitives (RNText, View, Pressable) —
 * never themed components like Text, Button, or anything that calls useTheme.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    // The componentStack names the exact component even in production
    // bundles — log it as a string so it survives console collapses.
    if (errorInfo && errorInfo.componentStack) {
      console.error('[ErrorBoundary] componentStack:', String(errorInfo.componentStack).slice(0, 4000));
    }
    // Report to APM (web-only — apm is a no-op before initApm runs on web;
    // on native the singleton never starts so captureError is a no-op).
    try {
      apm.captureError(error, { componentStack: errorInfo && errorInfo.componentStack ? String(errorInfo.componentStack).slice(0, 4000) : '' });
    } catch (_) {
      // Never let APM reporting crash the error boundary itself.
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <View style={styles.content}>
            <RNText style={styles.title}>
              Something went wrong
            </RNText>
            <RNText style={styles.message}>
              An unexpected error occurred. Please try again.
            </RNText>
            {__DEV__ && this.state.error && (
              <RNText style={styles.errorDetail}>
                {this.state.error.toString()}
              </RNText>
            )}
            <Pressable style={styles.retryButton} onPress={this.handleRetry}>
              <RNText style={styles.retryButtonText}>Try Again</RNText>
            </Pressable>
          </View>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: '#000',
  },
  content: {
    alignItems: 'center',
    maxWidth: 320,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'center',
    marginBottom: 8,
  },
  message: {
    fontSize: 16,
    color: '#aaa',
    textAlign: 'center',
    lineHeight: 24,
  },
  errorDetail: {
    marginTop: 16,
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    lineHeight: 18,
  },
  retryButton: {
    marginTop: 20,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: '#6c5ce7',
  },
  retryButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
  },
});

export default ErrorBoundary;
